/**
 * FI Notification Inbox Hooks (GH #2281)
 *
 * React Query hooks over the institutional notification inbox
 * (/api/v1/notifications, notification-inbox.openapi.yaml). Mirrors the
 * useTasks.ts conventions: a list hook, a lightweight polled unread-count
 * hook for the header bell badge (the spec has no /count op, so the badge
 * reads `unreadCount` off a pageSize=1 list call), and mutations that
 * invalidate the shared key root.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  type NotificationListResponse,
  type NotificationResponse,
  type MarkAllReadResponse,
  type NotificationListQueryOptions,
} from '../api/services/notifications';

// ---------------------------------------------------------------------------
// Query key constants
// ---------------------------------------------------------------------------

const NOTIFICATIONS_ROOT = 'admin';
const NOTIFICATIONS_KEY = 'notifications';

const notificationKeys = {
  all: [NOTIFICATIONS_ROOT, NOTIFICATIONS_KEY] as const,
  list: (params?: NotificationListQueryOptions) =>
    [NOTIFICATIONS_ROOT, NOTIFICATIONS_KEY, 'list', params] as const,
  unreadCount: () => [NOTIFICATIONS_ROOT, NOTIFICATIONS_KEY, 'unread-count'] as const,
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Hook for listing the tenant's FI notifications with optional filters and
 * cursor pagination. Pass `refetchIntervalMs` for surfaces that must stay
 * warm without user interaction (the header bell list).
 */
export function useNotifications(
  params?: NotificationListQueryOptions,
  options?: { refetchIntervalMs?: number; enabled?: boolean },
) {
  return useQuery<NotificationListResponse, Error>({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.list(params),
    refetchInterval: options?.refetchIntervalMs,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Lightweight polled unread-count for the header bell badge (the useTaskCount
 * pattern: 60s default poll, `enabled: false` to suppress for roles without
 * the surface). Fetches one row and reads the across-pages `unreadCount`.
 */
export function useUnreadNotificationCount(options?: {
  refetchIntervalMs?: number;
  enabled?: boolean;
}) {
  const refetchInterval = options?.refetchIntervalMs ?? 60_000;
  const enabled = options?.enabled ?? true;
  return useQuery<NotificationListResponse, Error>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationsApi.list({ pageSize: 1 }),
    refetchInterval,
    enabled,
  });
}

/**
 * Mutation hook: mark one notification read (idempotent server-side).
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation<NotificationResponse, Error, string>({
    mutationFn: (notificationId) => notificationsApi.markRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mutation hook: mark every visible unread notification read.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation<MarkAllReadResponse, Error, void>({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
